import { Confirm } from "/WebInterface/new-ui/modules/alert.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import store from "/WebInterface/new-ui/upload/js/reducer.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import {
  isFileOpen,
  blockUploads,
  unblockUploads,
  checkFileExist,
  makeDir,
} from "/WebInterface/new-ui/upload/utils/apis.js";

export const handleUpdateUploadingStatusCommon = (parent, uploading) => {
  const this_parent = parent;
  if (
    (uploading === true || uploading === false) &&
    uploading !== this_parent.fileUploadingInProgress
  ) {
    this_parent.fileUploadingInProgress = uploading;
    invoke("setUploadingStatus", [
      "isUploading",
      this_parent.fileUploadingInProgress,
    ]);
  }

  return this_parent.fileUploadingInProgress;
};

export const handleRetryUploading = async (
  parent,
  retries,
  cb,
  callback = () => {},
  retryCallback = () => {}
) => {
  const this_parent = parent;

  const fileInfo = this_parent.streamingFileInfo;
  const { id: fileId, nameF } = fileInfo;

  if (this_parent.retrying) return;
  this_parent.retrying = true;

  retryCallback();

  // clearInterval(this_parent.chunksInfo.intervalId);

  let retrySeconds = (retries + 1) * 10;
  let retryIntervalId;

  const setRetryTimer = (close) => {
    const intervalId = setInterval(() => {
      retrySeconds--;
      _$("#retrySeconds").text(retrySeconds);

      if (retrySeconds === 0) {
        this_parent.retrying = false;
        clearInterval(retryIntervalId);
        cb();
        close();
      }
    }, 1000);

    return intervalId;
  };

  const onBeforeShow = (root, close) => {
    retryIntervalId = setRetryTimer(close);
  };

  const onAfterShow = (root, close) => {
    _$("#skipFile").on("click", async () => {
      clearInterval(retryIntervalId);
      try {
        const resp = await invoke("confirmAlert", [
          {
            title: t("confirm"),
            body: t("areYouSureYouWantToCancelFileFromUploading", {
              fileName: nameF,
            }),
            confirm: t("yes"),
            cancel: t("no"),
            closable: true,
          },
        ]);
        if (resp) {
          close();
          this_parent.cancel();
          return;
        }

        retryIntervalId = setRetryTimer(close);
      } catch (error) {}
    });
  };

  const prepend = `<p class="text-primaryMain">Retrying uploading file <b>(${nameF})</b> In <span id="retrySeconds">${retrySeconds}</span> second(s)</p>`;

  try {
    const resp = await Confirm({
      title: "Retry",
      prepend,
      cancel: null,
      confirm: "Retry Now",
      closable: false,
      prependFooter: utils.generateHTML([
        {
          type: "button#skipFile.primary-button.is-bordered.mr-3",
          text: "Skip File",
        },
      ]),
      onBeforeShow,
      onAfterShow,
    });

    clearInterval(retryIntervalId);
    this_parent.retrying = false;

    if (resp) {
      if (retries < this_parent.maxRetries) {
        cb();
        return;
      }

      callback();
      handleFileOpenError(
        fileInfo,
        t("uploadingFailedWhileRetryingChunkUpload")
      );

      this_parent.nextFileInQueue(true);
      return;
    }
  } catch (error) {}
};

export const updateTimeStatusOfUploadingFileCommon = (
  resumeLoc = 0,
  parent
) => {
  const this_parent = parent;
  const { id: fileId, size } = this_parent.streamingFileInfo;

  this_parent.chunksInfo.speedHistory =
    this_parent.chunksInfo.speedHistory || [];

  // Ensure uploadStartTime is set if it's not already
  if (!this_parent.uploadStartTime) {
    this_parent.uploadStartTime = Date.now();
  }

  let lastUpdateTime = Date.now();
  const updateInterval = 1000;
  const speedWindowSeconds = 10;

  // Function to calculate and dispatch time status (can be called immediately or on interval)
  const calculateAndDispatchTimeStatus = (forceUpdate = false) => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTime;

    // If not forcing update and not enough time has passed, skip
    if (!forceUpdate && timeSinceLastUpdate < updateInterval) {
      return;
    }

    if (
      this_parent.isPaused ||
      this_parent.fileClosed ||
      this_parent.uploadDone
    ) {
      return;
    }

    if (!forceUpdate) {
      lastUpdateTime = now;
    }

    // Ensure uploadStartTime is set (fallback)
    if (!this_parent.uploadStartTime) {
      this_parent.uploadStartTime = now;
    }

    let elapsedTime = (now - this_parent.uploadStartTime) / 1000;

    if (this_parent.pauseLog && this_parent.pauseLog.length > 0) {
      const pausedFor = this_parent.pauseLog.reduce((sum, item) => {
        return sum + (item.pausedFor ? item.pausedFor : 0);
      }, 0);
      if (pausedFor > 0) {
        elapsedTime = elapsedTime - pausedFor / 1000;
      }
    }

    this_parent.chunksInfo.elapsedTime = elapsedTime;

    this_parent.chunksInfo.speedHistory.push({
      timestamp: now,
      bytes: this_parent.chunksInfo.bytesUploaded,
    });

    const tenSecondsAgo = now - speedWindowSeconds * 1000;
    this_parent.chunksInfo.speedHistory =
      this_parent.chunksInfo.speedHistory.filter(
        (entry) => entry.timestamp >= tenSecondsAgo
      );

    let speed = 0;
    const oldestEntry =
      this_parent.chunksInfo.speedHistory.length > 0
        ? this_parent.chunksInfo.speedHistory[0]
        : null;
    const newestEntry =
      this_parent.chunksInfo.speedHistory.length > 0
        ? this_parent.chunksInfo.speedHistory[
            this_parent.chunksInfo.speedHistory.length - 1
          ]
        : null;

    if (oldestEntry && newestEntry) {
      const bytesUploadedInWindow = newestEntry.bytes - oldestEntry.bytes;
      const timeWindowInSeconds =
        (newestEntry.timestamp - oldestEntry.timestamp) / 1000;

      if (timeWindowInSeconds > 0) {
        speed = bytesUploadedInWindow / timeWindowInSeconds;
      }
    }

    if (!Number.isFinite(speed) || speed < 0) {
      speed = 0;
    }

    const overAllSpeed =
      elapsedTime > 0 ? this_parent.chunksInfo.bytesUploaded / elapsedTime : 0;

    const speedToCheckTimeRemaining = overAllSpeed || speed;
    let timeRemaining;
    if (
      speedToCheckTimeRemaining > 0 &&
      Number.isFinite(speedToCheckTimeRemaining)
    ) {
      const remainingBytes =
        size - resumeLoc - this_parent.chunksInfo.bytesUploaded;
      if (Number.isFinite(remainingBytes) && remainingBytes >= 0) {
        timeRemaining = remainingBytes / speedToCheckTimeRemaining;
        if (!Number.isFinite(timeRemaining) || timeRemaining < 0) {
          timeRemaining = "-";
        }
      } else {
        timeRemaining = "-";
      }
    } else {
      timeRemaining = "-";
    }

    this_parent.chunksInfo.calculatedSpeed = speed;
    this_parent.chunksInfo.calculatedOverAllSpeed = overAllSpeed;
    this_parent.chunksInfo.calculatedTimeRemaining = timeRemaining;
  };

  let lastForceUpdate = 0;
  const forceUpdateThrottle = 200;
  this_parent.forceUpdateTimeStatus = () => {
    const now = Date.now();
    if (now - lastForceUpdate < forceUpdateThrottle) {
      return;
    }
    lastForceUpdate = now;
    calculateAndDispatchTimeStatus(true);
  };

  const updateUI = () => {
    if (
      this_parent.isPaused ||
      this_parent.fileClosed ||
      this_parent.uploadDone
    ) {
      return;
    }

    const now = Date.now();
    let elapsedTime = (now - this_parent.uploadStartTime) / 1000;

    if (this_parent.pauseLog && this_parent.pauseLog.length > 0) {
      const pausedFor = this_parent.pauseLog.reduce((sum, item) => {
        return sum + (item.pausedFor ? item.pausedFor : 0);
      }, 0);
      if (pausedFor > 0) {
        elapsedTime = elapsedTime - pausedFor / 1000;
      }
    }

    this_parent.chunksInfo.elapsedTime = elapsedTime;

    const speed = this_parent.chunksInfo.calculatedSpeed || 0;
    const overAllSpeed = this_parent.chunksInfo.calculatedOverAllSpeed || 0;
    const timeRemaining = this_parent.chunksInfo.calculatedTimeRemaining || "-";

    store.dispatch("updateFileTimeStatus", {
      fileId,
      speed,
      elapsedTime,
      timeRemaining,
      overAllSpeed,
    });
  };

  if (this_parent.chunksInfo.speedUpdateIntervalId) {
    clearInterval(this_parent.chunksInfo.speedUpdateIntervalId);
  }

  if (this_parent.chunksInfo.uiUpdateAnimationFrameId) {
    cancelAnimationFrame(this_parent.chunksInfo.uiUpdateAnimationFrameId);
    this_parent.chunksInfo.uiUpdateAnimationFrameId = null;
  }

  // Update only once per second: calculate and dispatch together
  this_parent.chunksInfo.speedUpdateIntervalId = setInterval(() => {
    if (
      !this_parent.isPaused &&
      !this_parent.fileClosed &&
      !this_parent.uploadDone
    ) {
      calculateAndDispatchTimeStatus(false);
      updateUI();
    }
  }, 1000);

  // Initial calculation and update
  calculateAndDispatchTimeStatus(false);
  updateUI();
};

export const pausingUpload = (parent) => {
  const this_parent = parent;

  const fileInfo = this_parent.streamingFileInfo;
  const { id: fileId, size } = fileInfo;

  this_parent.isPaused = true;

  const updatedFileToPausing = {
    ...fileInfo,
    status: "pausing",
    chunkPos: this_parent.chunksInfo.chunkPos,
  };

  store.dispatch("updateFileStatusAndRerenderFile", {
    [fileId]: updatedFileToPausing,
  });

  // Update file upload status in the store
  store.dispatch("updateFileUploadStatus", {
    fileId,
    size: this_parent.chunksInfo.bytesUploaded,
    maxSize: size,
  });
};

export const pauseUpload = (parent) => {
  const this_parent = parent;

  this_parent.pauseLog.push({
    pausedAt: Date.now(),
  });

  const fileInfo = this_parent.streamingFileInfo;
  const { id: fileId, size } = fileInfo;

  this_parent.handleUpdateUploadingStatus(false);

  const updatedFile = {
    ...fileInfo,
    status: "paused",
    chunkPos: this_parent.chunksInfo.chunkPos,
  };

  // Update file status to "paused" and chunk position in the store
  store.dispatch("updateFileStatusAndRerenderFile", {
    [fileId]: updatedFile,
  });

  // Clear the interval responsible for chunk upload
  clearInterval(this_parent.chunksInfo.intervalId);

  // Clear the speed update interval
  if (this_parent.chunksInfo.speedUpdateIntervalId) {
    clearInterval(this_parent.chunksInfo.speedUpdateIntervalId);
    this_parent.chunksInfo.speedUpdateIntervalId = null;
  }

  if (this_parent.chunksInfo.uiUpdateAnimationFrameId) {
    cancelAnimationFrame(this_parent.chunksInfo.uiUpdateAnimationFrameId);
    this_parent.chunksInfo.uiUpdateAnimationFrameId = null;
  }
  store.dispatch("updateFileUploadStatus", {
    fileId,
    size: this_parent.chunksInfo.bytesUploaded,
    maxSize: size,
  });

  // Update file time status in the store
  store.dispatch("updateFileTimeStatus", {
    fileId,
    speed: 0,
    elapsedTime: this_parent.chunksInfo.elapsedTime,
  });
};

export const resumeUpload = async (parent, onResume = () => {}) => {
  const this_parent = parent;

  const isDownloadInProgress = invoke("getDownloadStatus");

  if (isDownloadInProgress) {
    invoke("growl", [
      t("cantResumeUploadingTillDownloadingFinished"),
      t("downloadingInProgress"),
      5000,
      true,
    ]);
    return;
  }

  const fileInfo = this_parent.streamingFileInfo;
  const { id: fileId, size, reUploadId } = fileInfo;
  this_parent.isPaused = false;

  try {
    const resp = await isFileOpen({
      upload_id: reUploadId || fileId,
      random: Math.random(),
      chunkInfo: JSON.stringify({}),
    });

    if (resp.toLowerCase() === "open") {
      const updatedFile = {
        ...fileInfo,
        status: "uploading",
        chunkPos: this_parent.chunksInfo.chunkPos,
      };

      // Update file status and chunk position in the store
      store.dispatch("updateFileStatusAndRerenderFile", {
        [fileId]: updatedFile,
      });

      // Update file upload status in the store
      store.dispatch("updateFileUploadStatus", {
        fileId,
        size: this_parent.chunksInfo.bytesUploaded,
        maxSize: size,
      });

      onResume();

      if (this_parent.pauseLog.length > 0) {
        const logToUpdate =
          this_parent.pauseLog[this_parent.pauseLog.length - 1];
        if (!logToUpdate.resumedAt) {
          logToUpdate.resumedAt = Date.now();
          logToUpdate.pausedFor = Date.now() - logToUpdate.pausedAt;
        }
      }

      // Update the time status of the uploading file
      this_parent.updateTimeStatusOfUploadingFile(
        this_parent.chunksInfo.chunkPos
      );
    } else {
      invoke("growl", ["", t("cantResumeFile"), 4000, true]);
      C.error("Upload:Error occurred during resume operation:", error);
    }
  } catch (error) {
    // Handle any errors if necessary
    invoke("growl", ["", t("cantResumeFile"), 4000, true]);
    C.error("Upload:Error occurred during resume operation:", error);
  }
};

export const closeFileUsingBlockUnBlockUpload = async (
  preStatus = {
    status: "canceling",
  },
  postStatus = {
    status: "canceled",
  },
  beforeClosingFile = () => {},
  parent
) => {
  const this_parent = parent;

  const fileInfo = this_parent.streamingFileInfo;
  const { id: fileId, size, reUploadId } = fileInfo;

  // Track call count for this file
  if (!this_parent.closeFileUsingBlockUnBlockCallCount) {
    this_parent.closeFileUsingBlockUnBlockCallCount = 0;
  }
  this_parent.closeFileUsingBlockUnBlockCallCount++;
  const callAttempt = this_parent.closeFileUsingBlockUnBlockCallCount;

  // Log closeFileUsingBlockUnBlock called
  const uniqueContext = {
    fileId: reUploadId || fileId || "unknown",
    fileName: fileInfo?.nameF || "unknown",
    fileSize: size || 0,
    preStatus: preStatus?.status || "unknown",
    postStatus: postStatus?.status || "unknown",
    callAttempt: callAttempt,
    timestamp: Date.now(),
    alreadyClosed: this_parent.fileClosed || false,
  };

  C.logAction("file", "CloseFile called", uniqueContext, true); // Skip deduplication
  console.log(
    `[DEBUG] CloseFile called (attempt #${callAttempt}) with context:`,
    uniqueContext
  );

  // Check if already closed
  if (this_parent.fileClosed && callAttempt > 1) {
    C.logAction(
      "file",
      "CloseFile skipped (already closed)",
      {
        fileId: reUploadId || fileId || "unknown",
        callAttempt: callAttempt,
        timestamp: Date.now(),
      },
      true
    );
    return;
  }

  this_parent.fileClosed = true;

  if (this_parent.chunksInfo.speedUpdateIntervalId) {
    clearInterval(this_parent.chunksInfo.speedUpdateIntervalId);
    this_parent.chunksInfo.speedUpdateIntervalId = null;
  }

  if (this_parent.chunksInfo.uiUpdateAnimationFrameId) {
    cancelAnimationFrame(this_parent.chunksInfo.uiUpdateAnimationFrameId);
    this_parent.chunksInfo.uiUpdateAnimationFrameId = null;
  }

  beforeClosingFile();

  if (preStatus) {
    store.dispatch("updateFileStatusAndRerenderFile", {
      [fileId]: {
        ...fileInfo,
        ...preStatus,
      },
    });
  }

  store.dispatch("updateFileTimeStatus", {
    fileId,
    speed: 0,
    elapsedTime: this_parent.chunksInfo.elapsedTime,
  });

  store.dispatch("updateFileUploadStatus", {
    fileId,
    size: this_parent.chunksInfo.bytesUploaded,
    maxSize: size,
  });

  // Track blockFile retry attempts
  let blockFileAttempt = 0;

  const blockFile = async () => {
    blockFileAttempt++;
    const blockFileStartTime = Date.now();

    try {
      // Log blockUploads API call
      C.logAction(
        "file",
        "BlockUploads API called",
        {
          fileId: reUploadId || fileId || "unknown",
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      // Block uploads with random token
      console.log("Blocking uploads");
      const blockUploadsStartTime = Date.now();
      await blockUploads({
        fileId: reUploadId || fileId,
        random: Math.random(),
      });
      const blockUploadsDuration = Date.now() - blockUploadsStartTime;

      // Log blockUploads API success
      C.logAction(
        "file",
        "BlockUploads API success",
        {
          fileId: reUploadId || fileId || "unknown",
          duration: blockUploadsDuration,
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      // Log isFileOpen API call
      C.logAction(
        "file",
        "CheckFileOpen (in BlockUnBlock) called",
        {
          fileId: reUploadId || fileId || "unknown",
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      const checkFileOpenStartTime = Date.now();
      const fileStatus = await isFileOpen({
        upload_id: reUploadId || fileId,
        random: Math.random(),
        chunkInfo: JSON.stringify({}),
      });
      const checkFileOpenDuration = Date.now() - checkFileOpenStartTime;

      console.log("File status:", fileStatus);
      const fileOpen = fileStatus.toLowerCase().indexOf("open") >= 0;

      // Log isFileOpen API response
      C.logAction(
        "file",
        `CheckFileOpen (in BlockUnBlock) response: ${
          fileOpen ? "open" : "closed"
        }`,
        {
          fileId: reUploadId || fileId || "unknown",
          fileStatus: fileStatus,
          fileOpen: fileOpen,
          duration: checkFileOpenDuration,
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      if (fileOpen) {
        // Log retry attempt
        C.logAction(
          "file",
          "CloseFileUsingBlockUnBlock retry (file still open)",
          {
            fileId: reUploadId || fileId || "unknown",
            callAttempt: callAttempt,
            blockFileAttempt: blockFileAttempt,
            fileStatus: fileStatus,
            retryDelay: 1000,
            timestamp: Date.now(),
          },
          true
        );

        setTimeout(() => {
          blockFile();
        }, 1000);
        return;
      }

      // Update file status to "canceled" in the store
      if (postStatus) {
        store.dispatch("updateFileStatusAndRerenderFile", {
          [fileId]: {
            ...fileInfo,
            ...postStatus,
          },
        });
      }

      // Log unblockUploads API call
      C.logAction(
        "file",
        "UnblockUploads API called",
        {
          fileId: reUploadId || fileId || "unknown",
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      // Unblock uploads with another random token
      console.log("Unblocking uploads");
      const unblockUploadsStartTime = Date.now();
      await unblockUploads({ random: Math.random() });
      const unblockUploadsDuration = Date.now() - unblockUploadsStartTime;

      // Log unblockUploads API success
      C.logAction(
        "file",
        "UnblockUploads API success",
        {
          fileId: reUploadId || fileId || "unknown",
          duration: unblockUploadsDuration,
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      // Log overall success
      const totalDuration = Date.now() - blockFileStartTime;
      C.logAction(
        "file",
        "CloseFileUsingBlockUnBlock success",
        {
          fileId: reUploadId || fileId || "unknown",
          duration: totalDuration,
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          postStatus: postStatus?.status || "unknown",
          timestamp: Date.now(),
        },
        true
      );

      // Reset isPaused flag and proceed to the next file in the queue
      this_parent.nextFileInQueue(true);
    } catch (error) {
      // Log blockUploads API error
      const totalDuration = Date.now() - blockFileStartTime;
      C.logAction(
        "file",
        "CloseFileUsingBlockUnBlock error",
        {
          fileId: reUploadId || fileId || "unknown",
          error: error?.message || String(error),
          duration: totalDuration,
          callAttempt: callAttempt,
          blockFileAttempt: blockFileAttempt,
          timestamp: Date.now(),
        },
        true
      );

      // Handle any errors if necessary
      if (postStatus) {
        store.dispatch("updateFileStatusAndRerenderFile", {
          [fileId]: {
            ...fileInfo,
            ...postStatus,
            uploadingError: error?.message,
          },
        });
      }
      console.log("Upload:Error occurred during cancel operation:", error);
    }
  };
  blockFile();
};

export const handleFileOpenResponse = (parent, file, response, resumeLoc) => {
  if (response.toLowerCase().indexOf("error") >= 0) {
    parent.failFileOpen(file, response);
  } else {
    parent.startFileUpload(file, resumeLoc);
  }
};

export const failFileOpenCommon = (parent, file, response) => {
  handleFileOpenError(file, response);
  invoke("growl", ["", response, 5000, true]);
  parent.nextFileInQueue(true);
};

export const startFileUploadCommon = (parent, file, resumeLoc) => {
  if (file.size === 0) {
    C.log(`Upload: File is empty: ${file.nameF}, does not require upload`);
    parent.closeFileStream(file);
    return;
  }
  store.dispatch("updateFileStatusAndRerenderFile", {
    [file.id]: {
      ...file,
      status: "uploading",
    },
  });
  window.addEventListener("beforeunload", parent.beforeUnloadEvent);
  store.dispatch("showFileUploadStatus", {
    fileId: file.id,
    size: file.size,
    resumeLoc,
  });
  C.success(`Upload: File upload started: ${file.nameF}`);
  parent.updateTimeStatusOfUploadingFile(resumeLoc);
  parent.uploadTheNextSetOfChunk(resumeLoc);
};

export const handleCloseFileRetryCommon = async (parent, error) => {
  const fileInfo = parent.streamingFileInfo;
  const { nameF } = fileInfo;

  if (parent.closeFileRetries >= parent.maxCloseFileRetries) {
    return;
  }

  parent.closeFileRetries++;
  let retrySeconds = parent.closeFileRetries * 10;
  let retryIntervalId;

  const setRetryTimer = (close) => {
    const intervalId = setInterval(() => {
      retrySeconds--;
      _$("#retrySeconds").text(retrySeconds);

      if (retrySeconds === 0) {
        parent.retrying = false;
        clearInterval(retryIntervalId);
        parent.fileClosed = false;
        parent.closeFileStream(fileInfo);
        close();
      }
    }, 1000);

    return intervalId;
  };

  const onBeforeShow = (root, close) => {
    retryIntervalId = setRetryTimer(close);
  };

  const onAfterShow = (root, close) => {
    _$("#skipFile").on("click", async () => {
      clearInterval(retryIntervalId);
      try {
        const resp = await invoke("confirmAlert", [
          {
            title: t("confirm"),
            body: t("areYouSureYouWantToCancelFileFromClosing", {
              fileName: nameF,
            }),
            confirm: t("yes"),
            cancel: t("no"),
            closable: true,
          },
        ]);
        if (resp) {
          close();
          clearInterval(parent.chunksInfo.intervalId);
          C.log(`Upload: Clearing interval: ${parent.chunksInfo.intervalId}`);
          handleFileOpenError(fileInfo, error);
          invoke("growl", [error, t("fileClosingFailed"), 4000, true]);
          parent.nextFileInQueue(true);
          return;
        }

        retryIntervalId = setRetryTimer(close);
      } catch (error) {}
    });
  };

  const prepend = `<p class="text-primaryMain">Retrying closing file <b>(${nameF})</b> In <span id="retrySeconds">${retrySeconds}</span> second(s)</p>`;

  try {
    const resp = await Confirm({
      title: "Retry",
      prepend,
      cancel: null,
      confirm: "Retry Now",
      closable: false,
      prependFooter: utils.generateHTML([
        {
          type: "button#skipFile.primary-button.is-bordered.mr-3",
          text: "Skip File",
        },
      ]),
      onBeforeShow,
      onAfterShow,
    });

    clearInterval(retryIntervalId);
    parent.retrying = false;

    if (resp) {
      if (parent.closeFileRetries < parent.maxCloseFileRetries) {
        parent.fileClosed = false;
        parent.closeFileStream(fileInfo);
        return;
      }

      clearInterval(parent.chunksInfo.intervalId);
      C.log(`Upload: Clearing interval: ${parent.chunksInfo.intervalId}`);
      handleFileOpenError(fileInfo, error);
      invoke("growl", [error, t("fileClosingFailed"), 4000, true]);
      parent.nextFileInQueue(true);
      return;
    }
  } catch (error) {}
};

export const handleFileOpenError = (file, error) => {
  C.error(error);
  store.dispatch("updateFileStatusAndRerenderFile", {
    [file.id]: {
      ...file,
      status: "failed",
      uploadingError: error,
    },
  });
};

export const isCommandResponseError = (resp) => {
  if (!resp || resp.trim() === "") return false;

  const message = decodeURIComponent(resp).toLowerCase();
  if (message.includes("error")) return true;
  if (message.includes("failed")) return true;
  if (message.includes("not allowed")) return true;
  return false;
};

export const isMakeDirResponseValid = (resp) => !isCommandResponseError(resp);

export const tagDirectoryPrepError = (type, error) => {
  if (error?.type === "stat" || error?.type === "makedir") {
    return error;
  }
  const tagged =
    error instanceof Error ? error : new Error(error == null ? type : String(error));
  tagged.type = type;
  return tagged;
};

export const failDirectoryPrep = (parent, file, error) => {
  C.error(error);
  parent.nextFileInQueue(true);
  const uploadingError =
    error?.type === "makedir"
      ? t("failedToCreateDirectory")
      : t("fileUploadingFailed");
  handleFileOpenError(file, uploadingError);
};

const normalizeListedDirectoryPath = (p) => {
  if (p == null || p === "") return null;
  let s = String(p).trim();
  try {
    s = decodeURIComponent(s);
  } catch (ex) {}
  s = s.replace(/\/+/g, "/");
  if (!s.startsWith("/")) s = "/" + s;
  if (s !== "/" && !s.endsWith("/")) s += "/";
  return s;
};

const listedDirectorySegmentKey = (p) => {
  const n = normalizeListedDirectoryPath(p);
  if (!n) return "";
  return n.replace(/^\/+/, "").replace(/\/+$/, "").toLowerCase();
};

const listedPathCoversFolder = (listed, folder) => {
  const listedKey = listedDirectorySegmentKey(listed);
  const folderKey = listedDirectorySegmentKey(folder);
  if (!folderKey) return false;
  return listedKey === folderKey || listedKey.endsWith("/" + folderKey);
};

export const uploadDirectoryLevels = (nameF, isDir = false) => {
  const parts = String(nameF || "")
    .replace(/\\/g, "/")
    .split("/");
  if (parts.length < 3 && !isDir) return [];
  parts.pop();
  const levels = [];
  let acc = "";
  parts.filter(Boolean).forEach((segment) => {
    acc += `/${segment}`;
    levels.push(`${acc}/`.replace(/\/+/g, "/"));
  });
  return levels;
};

const ensureOneDirectory = async (folderPath, queue) => {
  const folder = utils.encodeURILocal(folderPath);
  if (queue.isDirectoryCreated(folder)) return;
  if (isCurrentListedDirectory(folder)) {
    queue.markDirectoryCreated(folder);
    return;
  }

  let prepType = "stat";
  try {
    const resp = await checkFileExist({
      path: folder,
      random: Math.random(),
    });
    if (isCommandResponseError(resp)) {
      throw tagDirectoryPrepError("stat", resp);
    }
    if (!resp) {
      prepType = "makedir";
      C.log(`Upload: Making directory: ${folder}`);
      const makeDirResp = await makeDir({ path: folder });
      if (isMakeDirResponseValid(makeDirResp)) {
        C.log(`Upload: Directory created: ${folder}`);
        queue.markDirectoryCreated(folder);
      } else {
        C.error(`Upload: Error occurred during makeDir: ${makeDirResp}`);
        throw tagDirectoryPrepError("makedir", makeDirResp);
      }
    } else {
      C.log(`Upload: Directory already exists: ${folder}`);
      queue.markDirectoryCreated(folder);
    }
  } catch (error) {
    C.error(`Upload: Error occurred during makeDir: ${error}`);
    throw tagDirectoryPrepError(error?.type || prepType, error);
  }
};

export const ensureUploadDirectory = async (nameF, isDir = false, queue) => {
  const levels = uploadDirectoryLevels(nameF, isDir);
  for (let index = 0; index < levels.length; index++) {
    await ensureOneDirectory(levels[index], queue);
  }
};

export const isCurrentListedDirectory = (encodedFolder) => {
  const folder = normalizeListedDirectoryPath(encodedFolder);
  if (!folder) return false;

  const dirInfo = invoke("getCurrentDirInfo");
  if (!dirInfo || dirInfo.failed || !dirInfo.path) return false;

  const listedPath = normalizeListedDirectoryPath(dirInfo.path);
  if (!listedPath) return false;

  const uiPath = normalizeListedDirectoryPath(invoke("currentPath"));
  if (uiPath && uiPath === folder) return true;

  if (listedPath === folder) return true;
  return listedPathCoversFolder(listedPath, folder);
};

/* Common Methods of XHR and WS */
export const getChunk = (file, start, size) => file.slice(start, start + size);

export const checkFilePartiallyUploaded = async (file) => {
  const { nameF, resumed } = file;

  if (!resumed) {
    return 0;
  }

  try {
    const resp = await checkFileExist({
      path: nameF,
      random: Math.random(),
    });
    return resp;
  } catch (error) {
    C.error(`Upload:Error occurred during checkFileExist: ${error}`);
    throw error;
  }
};

export const beforeUnloadEvent = (event) => {
  event.returnValue = "";
};

export const invoke = (method, params) => {
  const _parent =
    typeof self.window[method] === "function" ? self.window : parent.window;
  if (_parent[method]) {
    return _parent[method].apply(this, params);
  }
};
